import React, { type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { CloseGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'

export type NameTagProps = {
    children: ReactNode
    /** Renders a trailing clear affordance. Omit for read-only tags. */
    onRemove?: () => void
    removeAccessibilityLabel?: string
    className?: string
    testID?: string
}

// Figma "Mobile/NameTag" (node 6992:17395): 24px pill, 8px x-padding, fully
// rounded, graphics/dark-grey ground, P4 label in content/default. The removable
// form has no frame of its own — it keeps the pill and trims the right padding
// to seat the clear control.
const nameTagBase = 'h-6 shrink-0 items-center rounded-full bg-graphics-dark-grey'

export const NameTag = ({
    children,
    onRemove,
    removeAccessibilityLabel,
    className,
    testID,
}: NameTagProps) => (
    <HStack
        testID={testID}
        className={twMerge(
            nameTagBase,
            onRemove ? 'gap-1 pl-2 pr-1' : 'justify-center px-2',
            className,
        )}
    >
        {typeof children === 'string' || typeof children === 'number' ? (
            <Text size='caption' tone='default' numberOfLines={1}>
                {children}
            </Text>
        ) : (
            children
        )}
        {onRemove && (
            <Pressable
                accessibilityRole='button'
                accessibilityLabel={removeAccessibilityLabel}
                onPress={onRemove}
                className='h-4 w-4 shrink-0 items-center justify-center web:hover:opacity-70'
            >
                <CloseGlyph size={12} />
            </Pressable>
        )}
    </HStack>
)

NameTag.displayName = 'NameTag'
